import React, { createContext, useState, useCallback, ReactNode } from 'react';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationMessage {
  message: string;
  type: NotificationType;
  duration?: number; // Optional duration in ms
}

interface NotificationContextType {
  notification: NotificationMessage | null;
  showNotification: (message: string, type?: NotificationType, duration?: number) => void;
  hideNotification: () => void;
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notification, setNotification] = useState<NotificationMessage | null>(null);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const hideNotification = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setNotification(null);
  }, [timeoutId]);

  const showNotification = useCallback((message: string, type: NotificationType = 'info', duration: number = 5000) => {
    if (timeoutId) { // Clear existing timeout if a new notification comes in
      clearTimeout(timeoutId);
    }
    setNotification({ message, type });
    const newTimeoutId = setTimeout(() => {
      hideNotification();
    }, duration);
    setTimeoutId(newTimeoutId);
  }, [hideNotification, timeoutId]);

  return (
    <NotificationContext.Provider value={{ notification, showNotification, hideNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};